import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { Badge } from "../components/ui/Badge/Badge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { Modal } from "../components/ui/Modal/Modal";
import { Button } from "../components/ui/Button/Button";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import {
	useContractList,
	useContractEmailPreview,
	useSendContractEmail,
	useSaveContractMailTemplate,
	type ContractEmailPreview,
} from "../hooks/use-contracts";
import { formatDate } from "../lib/date";
import { apiUrl } from "../lib/api";
import styles from "./Contracts.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/contracts",
	component: withPermission(ContractsPage, "contracts", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
	}),
});

function ContractsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const searchParams = route.useSearch();
	const status = searchParams.status;
	const page = searchParams.page;
	const pageSize = searchParams.size;
	const [type, setType] = useState<string>("");
	const searchQuery = searchParams.search;
	const createdAtFrom = searchParams.createdAtFrom;
	const createdAtTo = searchParams.createdAtTo;
	const { data, isLoading, isError, refetch } = useContractList({
		status: status || undefined,
		type: type || undefined,
		search: searchQuery || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		page,
		limit: pageSize,
	});

	const contracts = data?.items ?? [];
	const total = data?.total ?? 0;

	const emailPreview = useContractEmailPreview();
	const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
	const [sendEmailContractId, setSendEmailContractId] = useState<string | null>(null);
	const [previewData, setPreviewData] = useState<ContractEmailPreview | null>(null);

	const openSendEmailModal = (contractId: string) => {
		setLoadingPreviewId(contractId);
		emailPreview.mutate(contractId, {
			onSuccess: (result) => {
				setPreviewData(result);
				setSendEmailContractId(contractId);
				setLoadingPreviewId(null);
			},
			onError: (err) => {
				setLoadingPreviewId(null);
				alert(err instanceof Error ? err.message : t("createFailed"));
			},
		});
	};

	const statusTabs = [
		{ key: "", label: t("all") },
		{ key: "draft", label: t("draft") },
		{ key: "active", label: t("active") },
		{ key: "expired", label: t("expired") },
		{ key: "terminated", label: t("terminated") },
		{ key: "renewed", label: t("renewed") },
	];

	return (
		<div className={styles.page}>
			<PageHeader title={t("contracts")} />

			<div className={styles.filters}>
				<div className={styles.tabs}>
					{statusTabs.map((tab) => (
						<button
							type="button"
							key={tab.key}
							className={[styles.tab, status === tab.key && styles.active]
								.filter(Boolean)
								.join(" ")}
							onClick={() => {
								navigate({ to: ".", search: { status: tab.key, page: 1, size: pageSize } });
							}}
						>
							{tab.label}
						</button>
					))}
				</div>
				<div className={styles.row}>
					<FilterBar
						filters={[
							{
								key: "type",
								type: "select",
								label: t("all"),
								options: [
									{ value: "sales", label: t("sales") },
									{ value: "maintenance", label: t("maintenance") },
									{ value: "rental", label: t("rental") },
									{ value: "service", label: t("service") },
								],
							},
							{ key: "search", type: "text", label: t("search") },
							{ key: "createdAt", type: "dateRange", label: t("createdAt") },
						]}
						values={{ type, search: searchQuery, createdAtFrom, createdAtTo }}
						onChange={(vals) => {
							setType(vals.type || "");
							navigate({
								to: ".",
								search: {
									status,
									search: vals.search || "",
									createdAtFrom: vals.createdAtFrom || "",
									createdAtTo: vals.createdAtTo || "",
									page: 1,
									size: pageSize,
								},
							});
						}}
					/>
				</div>
			</div>

			<Card className={styles.cardFill ?? ""} bodyClassName={styles.cardBodyFill ?? ""}>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						className={styles.tableScroll}
						columns={[
							{
								key: "documentNumber",
								header: t("contractDocumentNumber"),
								render: (row) => (
									<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
										<span>{row.documentNumber || row.id.slice(0, 8)}</span>
										{row.orderId ? (
											<Badge variant="primary">{t("order")}</Badge>
										) : row.offerId ? (
											<Badge variant="muted">{t("offer")}</Badge>
										) : null}
									</div>
								),
							},
							{
								key: "title",
								header: t("title"),
								render: (row) => row.title,
							},
							{
								key: "company",
								header: t("company"),
								render: (row) => row.companyName || row.companyId || "-",
							},
							{
								key: "type",
								header: t("type"),
								render: (row) => t(row.type),
							},
							{
								key: "startDate",
								header: t("startDate"),
								render: (row) => (row.startDate ? formatDate(row.startDate) : "-"),
							},
							{
								key: "endDate",
								header: t("endDate"),
								render: (row) => (row.endDate ? formatDate(row.endDate) : "-"),
							},
							{
								key: "status",
								header: t("status"),
								render: (row) => <StatusBadge status={row.status} type="contract" />,
							},
							{
								key: "downloadForm",
								header: t("downloadForm"),
								render: (row) => (
									<div className={styles.formLinkRow}>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(apiUrl(`/api/contracts/${row.id}/pdf?lang=en`), "_blank")
											}
										>
											EN
										</button>
										<button
											type="button"
											className={styles.formLink}
											onClick={() =>
												window.open(apiUrl(`/api/contracts/${row.id}/pdf?lang=tr`), "_blank")
											}
										>
											TR
										</button>
									</div>
								),
							},
							{
								key: "sendEmail",
								header: t("sendEmail"),
								render: (row) =>
									row.emailSentAt ? (
										<span title={`${t("sent")}: ${formatDate(row.emailSentAt)}`}>
											<Check size={18} className={styles.emailSentIcon} />
										</span>
									) : (
										<button
											type="button"
											className={styles.formLink}
											disabled={loadingPreviewId === row.id}
											onClick={() => openSendEmailModal(row.id)}
										>
											{loadingPreviewId === row.id ? (
												<span className={styles.miniSpinner} />
											) : (
												<Mail size={18} />
											)}
										</button>
									),
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/contracts/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={contracts}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(newPage) =>
						navigate({ to: ".", search: { status, page: newPage, size: pageSize } })
					}
					onPageSizeChange={(newSize) =>
						navigate({ to: ".", search: { status, page: 1, size: newSize } })
					}
				/>
			</Card>

			{sendEmailContractId && previewData && (
				<SendContractEmailModal
					contractId={sendEmailContractId}
					preview={previewData}
					onClose={() => {
						setSendEmailContractId(null);
						setPreviewData(null);
					}}
				/>
			)}
		</div>
	);
}

function SendContractEmailModal({
	contractId,
	preview,
	onClose,
}: {
	contractId: string;
	preview: ContractEmailPreview;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const sendEmail = useSendContractEmail();
	const saveTemplate = useSaveContractMailTemplate();
	const [email, setEmail] = useState(preview.email);
	const [cc, setCc] = useState(preview.cc);
	const [subject, setSubject] = useState(preview.subject);
	const [message, setMessage] = useState(preview.message);
	const [lang, setLang] = useState<"en" | "tr">(preview.defaultLang);
	const [previewLoading, setPreviewLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [hasCustomTemplate, setHasCustomTemplate] = useState(preview.hasCustomTemplate);
	const [templateNotice, setTemplateNotice] = useState<string | null>(null);

	const changeLang = (next: "en" | "tr") => {
		if (next === lang) return;
		setPreviewLoading(true);
		setLang(next);
	};

	const handleSaveTemplate = async () => {
		setTemplateNotice(null);
		const name = preview.recipientName.trim();
		if (name && (message.includes(name) || subject.includes(name))) {
			const proceed = window.confirm(
				`Kaydedeceğiniz metinde "${name}" geçiyor.\n\nBu metin bundan sonra tüm müşterilere aynen gideceği için bu firma adı diğer müşterilere de gönderilir.\n\nYine de kaydedilsin mi?`,
			);
			if (!proceed) return;
		}
		try {
			await saveTemplate.mutateAsync({ subject: subject.trim(), body: message });
			setHasCustomTemplate(true);
			setTemplateNotice("Varsayılan metniniz kaydedildi.");
		} catch (err) {
			setTemplateNotice(err instanceof Error ? err.message : "Kaydedilemedi");
		}
	};

	const handleResetTemplate = async () => {
		setTemplateNotice(null);
		try {
			await saveTemplate.mutateAsync({ subject: null, body: null });
			setHasCustomTemplate(false);
			setSubject("");
			setMessage("");
			setTemplateNotice("Varsayılanınız silindi.");
		} catch (err) {
			setTemplateNotice(err instanceof Error ? err.message : "Kaydedilemedi");
		}
	};

	const handleSend = async () => {
		setError(null);
		if (!email.trim()) {
			setError(t("requiredField"));
			return;
		}
		if (preview.signatureMissingReason) {
			const proceed = window.confirm(
				`${preview.signatureMissingReason}\n\nYine de gönderilsin mi?`,
			);
			if (!proceed) return;
		}
		try {
			await sendEmail.mutateAsync({
				id: contractId,
				email: email.trim(),
				cc: cc.trim(),
				subject: subject.trim(),
				message,
				lang,
			});
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : t("createFailed"));
		}
	};

	return (
		<Modal isOpen onClose={onClose} title={t("sendContractEmail")} maxWidth="1100px">
			<div className={styles.emailModalLayout}>
				<div className={styles.emailModalForm}>
					<FormField label={t("email")} required>
						<Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
					</FormField>
					<FormField label={t("emailCc")}>
						<Input value={cc} onChange={(e) => setCc(e.target.value)} />
					</FormField>
					<FormField label={t("downloadForm")}>
						<div className={styles.formLinkRow}>
							<button
								type="button"
								className={`${styles.langButton} ${lang === "en" ? styles.langButtonActive : ""}`}
								onClick={() => changeLang("en")}
							>
								EN
							</button>
							<button
								type="button"
								className={`${styles.langButton} ${lang === "tr" ? styles.langButtonActive : ""}`}
								onClick={() => changeLang("tr")}
							>
								TR
							</button>
						</div>
					</FormField>
					<FormField label={t("subject")}>
						<Input value={subject} onChange={(e) => setSubject(e.target.value)} />
					</FormField>
					<FormField label={t("message")}>
						<textarea
							className={styles.textarea}
							rows={8}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
						/>
					</FormField>
					<div className={styles.templateActions}>
						<button
							type="button"
							className={styles.formLink}
							disabled={saveTemplate.isPending}
							onClick={handleSaveTemplate}
						>
							☆ Bu konu ve mesajı varsayılanım yap
						</button>
						{hasCustomTemplate && (
							<button
								type="button"
								className={styles.formLink}
								disabled={saveTemplate.isPending}
								onClick={handleResetTemplate}
							>
								Varsayılanımı sil
							</button>
						)}
						{templateNotice && <span className={styles.templateNotice}>{templateNotice}</span>}
					</div>
					<p className={styles.templateHint}>
						{hasCustomTemplate
							? "Şu anda kendi kaydettiğiniz konu ve mesaj kullanılıyor. Metni değiştirip tekrar kaydederseniz varsayılanınız güncellenir; “Varsayılanımı sil” derseniz alanlar yine boş açılır."
							: "Konu ve mesaj boş geliyor. Yazdığınız metni “varsayılanım yap” ile kaydederseniz, bundan sonra açtığınız her sözleşme mailinde hazır gelir. Yalnızca sizin maillerinizi etkiler, diğer satışçılar kendi metnini kullanır."}
					</p>
					{error && <p className={styles.emailError}>{error}</p>}
					<div className={styles.modalActions}>
						<Button variant="ghost" onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button onClick={handleSend} isLoading={sendEmail.isPending}>
							{t("send")}
						</Button>
					</div>
				</div>
				<div className={styles.emailModalPreview}>
					{previewLoading && (
						<div className={styles.previewLoading}>
							<LoadingSpinner />
						</div>
					)}
					<iframe
						key={lang}
						src={apiUrl(`/api/contracts/${contractId}/pdf?lang=${lang}`)}
						title="contract-pdf-preview"
						className={styles.previewFrame}
						onLoad={() => setPreviewLoading(false)}
					/>
				</div>
			</div>
		</Modal>
	);
}
